/**
 * 列型変換のステップコンポーネント
 *
 * Step1: 設定画面 - 列ごとの変換設定
 * Step2: プレビュー画面 - 変換結果のプレビュー
 */

import React, { useState, useEffect } from 'react';
import { DataSet, EphemeralDataSet, DataValue, createDatasetId, hasData } from '../../types';
import { ColumnConversion, generateConversionSQL, generatePreviewSummarySQL } from '../utils/column-type-conversion';
import { executeOperation } from '../utils/executeOperation';
import { createTransformOperationId } from '../../types';
import { ROW_ID_COLUMN_NAME } from '../../types/system-columns';
import { DatasetAccessor } from '../utils/DatasetAccessor';
import logger from '../utils/logger';
import * as styles from '../styles/components/convert-column-types-dialog.css';

/**
 * Step1: 設定画面
 */
export interface Step1SettingsProps {
  dataset: DataSet;
  initialColumnId?: string;
  conversions: ColumnConversion[];
  onConversionsChange: (conversions: ColumnConversion[]) => void;
}

export function Step1Settings({
  dataset,
  initialColumnId,
  conversions,
  onConversionsChange
}: Step1SettingsProps) {
  const { DataType } = React.useMemo(() => ({
    DataType: [
      'boolean',
      'int64',
      'float64',
      'date',
      'datetime',
      'string'
    ] as const
  }), []);

  // 初期化：データセットの全列から変換設定を作成
  React.useEffect(() => {
    if (conversions.length === 0) {
      const initialConversions: ColumnConversion[] = dataset.schema.columns
        .filter(col => col.id !== 'ROW_ID')
        .map(col => ({
          columnId: col.id,
          columnName: col.name,
          fromType: col.type,
          toType: col.type,
          onError: 'null' as const,
          enabled: col.id === initialColumnId  // 右クリックした列のみチェック
        }));
      onConversionsChange(initialConversions);
    }
  }, [dataset, conversions.length, initialColumnId, onConversionsChange]);

  const handleToggle = (columnId: string) => {
    onConversionsChange(
      conversions.map(c =>
        c.columnId === columnId ? { ...c, enabled: !c.enabled } : c
      )
    );
  };

  const handleTypeChange = (columnId: string, newType: string) => {
    onConversionsChange(
      conversions.map(c =>
        c.columnId === columnId
          ? { ...c, toType: newType as typeof c.toType }
          : c
      )
    );
  };

  const handleErrorModeChange = (columnId: string, newMode: string) => {
    onConversionsChange(
      conversions.map(c =>
        c.columnId === columnId
          ? { ...c, onError: newMode as typeof c.onError }
          : c
      )
    );
  };

  return (
    <div className={styles.settingsContainer}>
      <div>
        <strong>Dataset:</strong> {dataset.name}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.conversionTable}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell} style={{ width: '50px' }}></th>
              <th className={styles.tableHeaderCell}>Column</th>
              <th className={styles.tableHeaderCell}>From</th>
              <th className={styles.tableHeaderCell}>To</th>
              <th className={styles.tableHeaderCell}>On Error</th>
            </tr>
          </thead>
          <tbody>
            {conversions.map(conv => (
              <tr key={conv.columnId} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <input
                    type="checkbox"
                    checked={conv.enabled}
                    onChange={() => handleToggle(conv.columnId)}
                    className={styles.checkbox}
                    data-testid={`checkbox-${conv.columnName}`}
                  />
                </td>
                <td className={styles.tableCell}>
                  <strong>{conv.columnName}</strong>
                </td>
                <td className={styles.tableCell}>{conv.fromType}</td>
                <td className={styles.tableCell}>
                  <select
                    value={conv.toType}
                    onChange={(e) => handleTypeChange(conv.columnId, e.target.value)}
                    className={styles.select}
                    data-testid={`type-select-${conv.columnName}`}
                  >
                    {DataType.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </td>
                <td className={styles.tableCell}>
                  <select
                    value={conv.onError}
                    onChange={(e) => handleErrorModeChange(conv.columnId, e.target.value)}
                    className={styles.select}
                    data-testid={`error-mode-select-${conv.columnName}`}
                  >
                    <option value="null">NULL</option>
                    <option value="exclude">Exclude row</option>
                    <option value="fail">Fail</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Step2: プレビュー画面
 */
export interface Step2PreviewProps {
  dataset: DataSet;
  conversions: ColumnConversion[];
  mode?: 'sample' | 'full';  // サンプルプレビュー or 全量プレビュー
}

interface PreviewSummary {
  [key: string]: {
    ok: number;
    fail: number;
  };
}

export function Step2Preview({
  dataset,
  conversions,
  mode = 'full'  // デフォルトは全量（ダイアログとの互換性）
}: Step2PreviewProps) {
  const [previewData, setPreviewData] = useState<EphemeralDataSet | null>(null);
  const [summary, setSummary] = useState<PreviewSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);  // 初期状態をtrueに変更
  const [error, setError] = useState<string | null>(null);
  const [showExcludedRows, setShowExcludedRows] = useState(false);

  /**
   * 型変換が失敗したかどうかを判定する
   *
   * DuckDBのTRY_CASTは変換失敗時にNULLを返す仕様を利用して判定：
   * - 変換前の値がNULLでない
   * - 変換後の値がNULL
   * → この場合、変換に失敗したと判断
   *
   * 注意: 元々NULLだった値は変換失敗とはみなさない
   *
   * @param originalValue - 変換前の値（undefinedは許容しない）
   * @param convertedValue - TRY_CAST実行後の値（undefinedは許容しない）
   * @returns 変換が失敗した場合true
   */
  const isConversionFailed = (originalValue: DataValue, convertedValue: DataValue): boolean => {
    return originalValue !== null && convertedValue === null;
  };

  // プレビュークエリを実行
  useEffect(() => {
    // ローディング状態を即座にセット
    setIsLoading(true);
    setError(null);

    // サンプルモードの場合のみdebounce処理（300ms）
    const debounceDelay = mode === 'sample' ? 300 : 0;
    const timerId = setTimeout(async () => {

      try {
        // 変換SQLを生成
        const { sql: conversionSQL } = generateConversionSQL(
          dataset.name,
          dataset.schema.columns,
          conversions
        );

        // サンプルモードの場合はLIMIT 100を追加
        const previewSQL = mode === 'sample' ? `${conversionSQL} LIMIT 100` : conversionSQL;

        logger.debug(`[Step2Preview] Generated ${mode} preview SQL`, { sql: previewSQL });

        // SQLクエリを実行
        const result = await executeOperation(
          {
            id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
            type: 'sql_query',
            query: previewSQL,
            targetDatasetId: dataset.id
          },
          { [dataset.id]: dataset },
          dataset.id
        );

        // プレビューデータを作成（一時的なデータセット）
        const previewDataset: EphemeralDataSet = {
          id: createDatasetId(`dataset-preview-${crypto.randomUUID()}`),
          name: `${dataset.name} (Preview)`,
          type: 'ephemeral',
          schema: result.schema,
          data: result.data ?? {},
          rowCount: result.rowCount ?? 0,
          metadata: {
            createdAt: new Date().toISOString(),
            purpose: 'temporary_analysis',
            description: 'Type conversion preview',
            sourceDatasetId: dataset.id
          }
        };

        setPreviewData(previewDataset);

        // サマリークエリを実行（統計情報）
        const summarySQL = generatePreviewSummarySQL(dataset.name, conversions);
        if (summarySQL) {
          // サンプルモードの場合はLIMIT 100のサブクエリから統計を取る
          const summarySQLFinal = mode === 'sample'
            ? summarySQL.replace(
                `FROM ${dataset.name}`,
                `FROM (SELECT * FROM ${dataset.name} LIMIT 100) AS sample`
              )
            : summarySQL;

          const summaryResult = await executeOperation(
            {
              id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
              type: 'sql_query',
              query: summarySQLFinal,
              targetDatasetId: dataset.id
            },
            { [dataset.id]: dataset },
            dataset.id
          );

          // サマリーデータを変換
          const summaryData: PreviewSummary = {};
          if (summaryResult.data && summaryResult.schema.columns.length > 0) {
            const row = 0;  // サマリーは1行のみ
            summaryResult.schema.columns.forEach(col => {
              const colData = summaryResult.data?.[col.id];
              if (colData && col.name.endsWith('_ok')) {
                const baseName = col.name.replace('_ok', '');
                if (!summaryData[baseName]) {
                  summaryData[baseName] = { ok: 0, fail: 0 };
                }
                summaryData[baseName].ok = Number(colData[row]);
              } else if (colData && col.name.endsWith('_fail')) {
                const baseName = col.name.replace('_fail', '');
                if (!summaryData[baseName]) {
                  summaryData[baseName] = { ok: 0, fail: 0 };
                }
                summaryData[baseName].fail = Number(colData[row]);
              }
            });
          }

          setSummary(summaryData);
        }

      } catch (err) {
        logger.error('[Step2Preview] Preview execution failed', { error: err });
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred');
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceDelay);

    // クリーンアップ: タイマーをキャンセル
    return () => clearTimeout(timerId);
  }, [dataset, conversions, mode]);

  if (isLoading) {
    return (
      <div className={styles.previewContainer}>
        <p>Loading preview...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.previewContainer}>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className={styles.previewContainer}>
        <p>No preview data available</p>
      </div>
    );
  }

  // 有効な変換のみ
  const activeConversions = conversions.filter(c => c.enabled && c.fromType !== c.toType);

  // datasetはPrimaryDataSetまたはEphemeralDataSetであるべき（常にdataを持つ）
  if (!hasData(dataset)) {
    return (
      <div className={styles.previewContainer}>
        <p style={{ color: 'red' }}>
          Error: Dataset does not have data loaded. Type conversion requires a dataset with data.
        </p>
      </div>
    );
  }

  // DatasetAccessorを使って列名から列IDを取得
  const datasetAccessor = new DatasetAccessor(dataset);
  const previewAccessor = new DatasetAccessor(previewData);

  // 統計情報を計算
  const totalRows = previewData.rowCount;
  const totalConversions = activeConversions.reduce((sum, conv) => {
    const stats = summary?.[conv.columnName];
    return sum + (stats?.ok || 0);
  }, 0);
  const totalNulls = activeConversions.reduce((sum, conv) => {
    const stats = summary?.[conv.columnName];
    return sum + (stats?.fail || 0);
  }, 0);
  // 除外される行数を計算（重複を除く）
  // 1行で複数列が変換失敗しても1行としてカウント
  const excludedRows = activeConversions.some(c => c.onError === 'exclude') && previewData
    ? Array.from({ length: previewData.rowCount ?? 0 }).filter((_, rowIndex) => {
        // この行がいずれかのexclude列で変換失敗しているかチェック
        return activeConversions.some(conv => {
          if (conv.onError !== 'exclude') return false;

          const originalValue = datasetAccessor.getValue(rowIndex, conv.columnName);
          const convertedValue = previewAccessor.getValue(rowIndex, conv.columnName);

          return isConversionFailed(originalValue, convertedValue);
        });
      }).length
    : 0;

  return (
    <div className={styles.previewContainer}>
      {/* 統計情報 */}
      <div className={styles.previewStats}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>
            {mode === 'sample' ? 'Sample Rows (LIMIT 100)' : 'Total Rows'}
          </div>
          <div className={styles.statValue}>{totalRows}</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>Successful Conversions</div>
          <div className={styles.statValue}>{totalConversions}</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>NULL Conversions</div>
          <div className={styles.statValue}>{totalNulls}</div>
        </div>
        {excludedRows > 0 && (
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Excluded Rows</div>
            <div className={styles.statValue}>{excludedRows}</div>
          </div>
        )}
      </div>

      {/* 凡例 */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#fff3cd' }}></div>
          <span>NULL (conversion failed)</span>
        </div>
        {excludedRows > 0 && (
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: '#f8d7da' }}></div>
            <span>Excluded row</span>
          </div>
        )}
      </div>

      {/* プレビューテーブル */}
      <div className={styles.previewTableContainer}>
        <table className={styles.conversionTable}>
          <thead className={styles.tableHeader}>
            <tr>
              {previewData.schema.columns
                .filter(col => col.name !== ROW_ID_COLUMN_NAME)
                .map(col => (
                  <th key={col.id} className={styles.tableHeaderCell}>
                    {col.name}
                    <br />
                    <span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                      ({col.type})
                    </span>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: previewData.rowCount ?? 0 }).map((_, rowIndex) => {
              // この行が除外されるかチェック
              // excludeモードの列で変換失敗した行は除外対象
              const isExcluded = activeConversions.some(conv => {
                if (conv.onError !== 'exclude') return false;

                // DatasetAccessorを使って列名から値を取得
                const originalValue = datasetAccessor.getValue(rowIndex, conv.columnName);
                const convertedValue = previewAccessor.getValue(rowIndex, conv.columnName);

                return isConversionFailed(originalValue, convertedValue);
              });

              return (
                <tr
                  key={rowIndex}
                  className={`${styles.tableRow} ${isExcluded ? styles.redRow : ''}`}
                >
                  {previewData.schema.columns
                    .filter(col => col.name !== ROW_ID_COLUMN_NAME)
                    .map(col => {
                      // 元データに存在しない列はスキップ
                      if (!datasetAccessor.hasColumn(col.name)) {
                        return null;
                      }

                      // DatasetAccessorを使って列名から値を取得
                      const originalValue = datasetAccessor.getValue(rowIndex, col.name);
                      const convertedValue = previewAccessor.getValue(rowIndex, col.name);

                      const conv = activeConversions.find(c => c.columnName === col.name);
                      const wasConverted = conv ? isConversionFailed(originalValue, convertedValue) : false;

                      return (
                        <td
                          key={col.id}
                          className={`${styles.tableCell} ${wasConverted ? styles.yellowCell : ''}`}
                        >
                          {convertedValue === null ? 'NULL' : String(convertedValue)}
                        </td>
                      );
                    })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 除外行を表示するチェックボックス */}
      {excludedRows > 0 && (
        <div className={styles.previewControls}>
          <label>
            <input
              type="checkbox"
              checked={showExcludedRows}
              onChange={(e) => setShowExcludedRows(e.target.checked)}
            />
            Show excluded rows
          </label>
        </div>
      )}
    </div>
  );
}
